import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  FormControl
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useOrg } from '../contexts/OrgContext';

function OrgSwitcher() {
  const { currentOrgId, organizations, orgsLoaded, refreshOrganizations, switchToOrg } = useOrg();
  const [loading, setLoading] = useState(false);

  // Orgs are loaded eagerly by OrgContext. Only refresh if not yet loaded
  // when this component mounts (edge case).
  useEffect(() => {
    if (!orgsLoaded) {
      setLoading(true);
      refreshOrganizations().finally(() => setLoading(false));
    }
  }, [orgsLoaded, refreshOrganizations]);

  const handleChange = (event) => {
    switchToOrg(parseInt(event.target.value));
  };

  const getCurrentOrg = () => {
    if (currentOrgId === null) return null;
    return organizations.find(o => o.id === currentOrgId);
  };

  const currentOrg = getCurrentOrg();

  const selectValue = currentOrg ? currentOrgId : '';
  const getRoleLabel = (org) => org?.user_role === 'system_admin' ? 'sys admin' : org?.user_role;
  const getRoleColor = (org) => (
    org?.user_role === 'system_admin' ? 'secondary' :
    org?.user_role === 'owner' ? 'error' :
      org?.user_role === 'admin' ? 'warning' :
        'default'
  );
  const isParentOrg = (org) => org?.account_type === 'parent' || org?.is_operational_workspace === false;
  const renderRoleChip = (org) => (
    <Chip
      label={getRoleLabel(org)}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.65rem',
        textTransform: 'uppercase',
        flexShrink: 0
      }}
      color={getRoleColor(org)}
    />
  );

  return (
    <FormControl size="small" sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
      <Select
        value={selectValue}
        onChange={handleChange}
        disabled={loading}
        displayEmpty
        renderValue={(selected) => {
          const selectedOrg = organizations.find((org) => org.id === selected);
          if (!selectedOrg) return null;
          const isParent = isParentOrg(selectedOrg);

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
              {isParent ? (
                <AccountTreeIcon fontSize="small" sx={{ color: '#8888a0', flexShrink: 0 }} />
              ) : (
                <BusinessIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0, flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {selectedOrg.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  {isParent && (
                    <Chip
                      label="parent"
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', textTransform: 'uppercase', flexShrink: 0 }}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {renderRoleChip(selectedOrg)}
                </Box>
              </Box>
            </Box>
          );
        }}
        sx={{
          color: '#fff',
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 42,
            py: 0.75,
            whiteSpace: 'normal'
          },
          '& .MuiSvgIcon-root': {
            color: '#8888a0',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.15)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.3)',
          },
        }}
      >
        {/* Personal option removed — org-only mode */}

        {Array.isArray(organizations) && organizations.map((org) => {
          const isParent = isParentOrg(org);
          return (
          <MenuItem key={org.id} value={org.id} disabled={isParent}>
            {isParent ? (
              <AccountTreeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            ) : (
              <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', ml: org.parent_account_id ? 2 : 0 }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, overflow: 'hidden' }}>
              <Typography variant="body2" noWrap sx={{ color: 'inherit' }}>{org.name}</Typography>
              {isParent && (
                <Chip
                  label="parent"
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', textTransform: 'uppercase', flexShrink: 0 }}
                  color="primary"
                  variant="outlined"
                />
              )}
              {renderRoleChip(org)}
            </Box>
          </MenuItem>
          );
        })}
      </Select>

    </FormControl>
  );
}

export default OrgSwitcher;
